<script setup lang="ts" generic="T extends SelectItemId = SelectItemId">
import type { SelectItemId, SelectItemType } from '#imports';

interface IProps {
    item: SelectItemType<T>;
    focused?: boolean;
}
const { item, focused } = defineProps<IProps>();
const emit = defineEmits<{
    click: [event: MouseEvent];
    mouseover: [event: MouseEvent];
}>();
const modelValue = defineModel<boolean>();
const router = useRouter();
const root = ref<HTMLDivElement>();
const onClick = (event: MouseEvent) => {
    if (item.isGroup) {
        return;
    }
    if (item) {
        emit('click', event);
        if (item.link) {
            router.push(item.link);
        } else {
            modelValue.value = !modelValue.value;
        }
    }
};
</script>

<template>
    <mr-checkbox
        v-if="item?.isCheckbox"
        v-model="modelValue"
        :label="item.label"
        :disabled="item.disabled"
    />
    <div
        v-else
        ref="root"
        class="select-item-container"
        :class="{
            group: item.isGroup,
            selected: modelValue,
            disabled: item.disabled,
            'sub-item': item.isSubItem,
            'with-sub-label': item.subLabel,
            focused,
        }"
        @click="onClick"
        @mouseover="emit('mouseover', $event)"
    >
        <mr-icon v-if="item.icon" class="icon" :icon="item.icon" />
        <img
            v-else-if="item.image !== undefined"
            class="image"
            :src="item.image"
            alt="image"
        />
        <div class="info-container">
            <div class="title-container">
                <div class="select-label">
                    {{ item.label }}
                </div>
                <div v-if="item.subTitle" class="sub-title">
                    {{ item.subTitle }}
                </div>
            </div>
            <div v-if="item.subLabel" class="select-sub-label">
                {{ item.subLabel }}
            </div>
        </div>
        <mr-divider v-if="item.isGroup" :thickness="1" class="mr-divider" />
    </div>
</template>

<style lang="scss" scoped>
.select-item-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 48px;
    padding: 0 12px;
    .mr-divider {
        margin: 20px;
    }
    &.focused:not(.group) {
        background-color: usePalette($p-blue, $p-100);
    }
    &:hover:not(.group) {
        cursor: pointer;
        .info-container {
            .title-container {
                .select-label {
                    color: usePalette($p-blue, $p-500);
                }
            }
        }
        .icon {
            color: usePalette($p-blue, $p-500) !important;
        }
    }
    &.with-sub-label {
        height: 80px !important;
    }
    &.sub-item {
        background-color: usePalette($p-cool-gray, $p-100);
        padding-right: 20px;
        .icon {
            transform: scaleX(-1);
        }
    }
    &.group {
        height: 48px;
        cursor: default;
        .select-label {
            @include tg-m($w5, usePalette($p-cool-gray, $p-500));
        }
    }
    &.disabled {
        cursor: not-allowed;
        .select-label {
            color: usePalette($p-blue, $p-500);
        }
        .icon {
            color: usePalette($p-blue, $p-500);
        }
        .image {
            filter: grayscale(50);
        }
    }
    &.selected {
        .select-label {
            color: usePalette($p-blue, $p-500);
        }
        .icon {
            color: usePalette($p-blue, $p-500) !important;
        }
        background: usePalette($p-blue, $p-200);
    }
    .info-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        .title-container {
            display: flex;
            align-items: center;
            .select-label {
                @include tg-l($w5);
                margin-left: 8px;
                white-space: nowrap;
            }
            .sub-title {
                @include tg-l($w5, usePalette($p-cool-gray, $p-500));
                white-space: nowrap;
            }
        }
        .select-sub-label {
            @include tg-s($w7, usePalette($p-cool-gray, $p-500));
            margin-left: 8px;
            white-space: nowrap;
        }
    }
    .icon {
        height: 20px;
        width: 20px;
        color: usePalette() !important;
        margin-left: 10px;
    }
    .image {
        width: 30px;
        height: 30px;
        background: usePalette($p-gray, $p-300);
        border-radius: 5px;
        margin-left: 10px;
    }
}
</style>
